// Generic icon glyphs (viewBox 0 0 20 20). Ported from the design bundle's primitives.jsx.
import type { CSSProperties } from "react";

type IconName =
	| "home" | "list" | "bank" | "pie" | "grid" | "settings" | "search" | "plus"
	| "chevR" | "chevD" | "chevU" | "arrowUp" | "arrowDn" | "filter" | "sort"
	| "more" | "check" | "x" | "sparkle" | "bell" | "upload" | "download"
	| "edit" | "trash" | "split" | "link" | "star" | "trophy" | "leaf"
	| "target" | "send" | "refresh" | "calendar" | "eye";

type IconProps = { name: IconName; size?: number; color?: string };

export function Icon({ name, size = 16, color = "currentColor" }: IconProps) {
	const stroke = {
		stroke: color,
		strokeWidth: 1.6,
		fill: "none" as const,
		strokeLinecap: "round" as const,
		strokeLinejoin: "round" as const,
	};
	const paths: Record<IconName, React.ReactNode> = {
		home: <path d="M3 10 L10 4 L17 10 V16 H3 Z" {...stroke} />,
		list: (
			<>
				<line x1="5" y1="6" x2="16" y2="6" {...stroke} />
				<line x1="5" y1="10" x2="16" y2="10" {...stroke} />
				<line x1="5" y1="14" x2="16" y2="14" {...stroke} />
			</>
		),
		bank: (
			<>
				<path d="M3 8 L10 3 L17 8" {...stroke} />
				<line x1="3" y1="9" x2="17" y2="9" {...stroke} />
				<line x1="6" y1="11" x2="6" y2="15" {...stroke} />
				<line x1="10" y1="11" x2="10" y2="15" {...stroke} />
				<line x1="14" y1="11" x2="14" y2="15" {...stroke} />
				<line x1="3" y1="16" x2="17" y2="16" {...stroke} />
			</>
		),
		pie: (
			<>
				<circle cx="10" cy="10" r="7" {...stroke} />
				<path d="M10 10 V3" {...stroke} />
				<path d="M10 10 L16 12.6" {...stroke} />
			</>
		),
		grid: (
			<>
				<rect x="3" y="3" width="6" height="6" {...stroke} />
				<rect x="11" y="3" width="6" height="6" {...stroke} />
				<rect x="3" y="11" width="6" height="6" {...stroke} />
				<rect x="11" y="11" width="6" height="6" {...stroke} />
			</>
		),
		settings: (
			<>
				<circle cx="10" cy="10" r="2.5" {...stroke} />
				<path d="M10 3 V5 M10 15 V17 M3 10 H5 M15 10 H17 M5.5 5.5 L6.7 6.7 M13.3 13.3 L14.5 14.5 M5.5 14.5 L6.7 13.3 M13.3 6.7 L14.5 5.5" {...stroke} />
			</>
		),
		search: (
			<>
				<circle cx="9" cy="9" r="5" {...stroke} />
				<line x1="13" y1="13" x2="16" y2="16" {...stroke} />
			</>
		),
		plus: (
			<>
				<line x1="10" y1="4" x2="10" y2="16" {...stroke} />
				<line x1="4" y1="10" x2="16" y2="10" {...stroke} />
			</>
		),
		chevR: <path d="M8 4 L14 10 L8 16" {...stroke} />,
		chevD: <path d="M4 8 L10 14 L16 8" {...stroke} />,
		chevU: <path d="M4 12 L10 6 L16 12" {...stroke} />,
		arrowUp: <path d="M10 16 V4 M5 9 L10 4 L15 9" {...stroke} />,
		arrowDn: <path d="M10 4 V16 M5 11 L10 16 L15 11" {...stroke} />,
		filter: <path d="M3 5 H17 L12 11 V16 L8 14 V11 L3 5 Z" {...stroke} />,
		sort: <path d="M5 4 V14 M3 12 L5 14 L7 12 M13 16 V6 M11 8 L13 6 L15 8" {...stroke} />,
		more: (
			<>
				<circle cx="5" cy="10" r="1" fill={color} />
				<circle cx="10" cy="10" r="1" fill={color} />
				<circle cx="15" cy="10" r="1" fill={color} />
			</>
		),
		check: <path d="M4 10 L8 14 L16 6" {...stroke} />,
		x: <path d="M5 5 L15 15 M15 5 L5 15" {...stroke} />,
		sparkle: <path d="M10 3 L11 8 L16 9 L11 10 L10 15 L9 10 L4 9 L9 8 Z" fill={color} />,
		bell: (
			<>
				<path d="M5 14 H15 L13 12 V8 A3 3 0 0 0 7 8 V12 Z" {...stroke} />
				<path d="M9 16 A1 1 0 0 0 11 16" {...stroke} />
			</>
		),
		upload: <path d="M10 14 V4 M5 9 L10 4 L15 9 M4 16 H16" {...stroke} />,
		download: <path d="M10 4 V14 M5 9 L10 14 L15 9 M4 16 H16" {...stroke} />,
		edit: <path d="M4 16 H8 L16 8 L12 4 L4 12 Z M11 5 L15 9" {...stroke} />,
		trash: <path d="M4 6 H16 M7 6 V4 H13 V6 M6 6 V16 H14 V6" {...stroke} />,
		split: <path d="M10 3 V8 M5 13 L10 8 L15 13 M5 13 V17 M15 13 V17" {...stroke} />,
		link: <path d="M9 11 L11 9 M7 13 A2.8 2.8 0 0 1 7 9 L9 7 A2.8 2.8 0 0 1 13 7 M13 13 A2.8 2.8 0 0 1 9 13 L11 11 A2.8 2.8 0 0 1 11 7" {...stroke} />,
		star: <path d="M10 3 L12 8 L17 8 L13 11 L14.5 16 L10 13 L5.5 16 L7 11 L3 8 L8 8 Z" {...stroke} />,
		trophy: <path d="M6 4 H14 V8 A4 4 0 0 1 6 8 Z M4 5 H6 V8 A2 2 0 0 1 4 7 Z M16 5 H14 V8 A2 2 0 0 0 16 7 Z M8 11 H12 V14 H8 Z M6 16 H14" {...stroke} />,
		leaf: <path d="M16 4 C 10 4, 4 8, 4 14 L 4 16 L 6 16 C 12 16, 16 12, 16 6 Z M 16 4 L 4 16" {...stroke} />,
		target: (
			<>
				<circle cx="10" cy="10" r="6" {...stroke} />
				<circle cx="10" cy="10" r="2.5" {...stroke} />
				<circle cx="10" cy="10" r="0.5" fill={color} />
			</>
		),
		send: <path d="M3 10 L17 4 L11 17 L9 11 Z" {...stroke} />,
		refresh: <path d="M16 6 V3 M16 6 H13 M16 6 A6 6 0 1 0 16 14" {...stroke} />,
		calendar: (
			<>
				<rect x="3" y="5" width="14" height="12" rx="1" {...stroke} />
				<line x1="3" y1="9" x2="17" y2="9" {...stroke} />
				<line x1="7" y1="3" x2="7" y2="6" {...stroke} />
				<line x1="13" y1="3" x2="13" y2="6" {...stroke} />
			</>
		),
		eye: (
			<>
				<path d="M2 10 C 4 6, 7 4, 10 4 C 13 4, 16 6, 18 10 C 16 14, 13 16, 10 16 C 7 16, 4 14, 2 10 Z" {...stroke} />
				<circle cx="10" cy="10" r="2" {...stroke} />
			</>
		),
	};
	const svgStyle: CSSProperties = { flex: "none" };
	return (
		<svg width={size} height={size} viewBox="0 0 20 20" style={svgStyle} aria-hidden="true">
			{paths[name] ?? null}
		</svg>
	);
}
